// basicsearchtable.js - REFACRED for Table Selection and Add Button
import * as React from 'react';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { visuallyHidden } from '@mui/utils';
// Import the specific icons needed
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart';

import { Button } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import PivotList from './pivotList';
import { useAuth } from '../../context/AuthContext';

//IsFavorite styling
import StarBorderIcon from '@mui/icons-material/StarBorder'; // Outlined star
import StarIcon from '@mui/icons-material/Star'; // Filled star


// --- Styled Components ---
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.common.black,
        color: '#E0E0E0',
        fontWeight: 'bold',
        '& .MuiTableSortLabel-root': {
            color: '#E0E0E0',
            '&:hover': {
                color: '#F5F5F5',
            },
            '&.Mui-active': {
                color: '#F5F5F5',
            },
        },
        '& .MuiTableSortLabel-root.Mui-active .MuiTableSortLabel-icon': {
            color: '#F5F5F5',
        },
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));
// --- End Styled Components ---


// --- Sorting Helpers ---
function descendingComparator(a, b, orderBy) {
    // Handle null or undefined values for valueA
    const valueA = a[orderBy] === null || a[orderBy] === undefined
        ? (typeof a[orderBy] === 'string' ? '' : -Infinity) // If string, treat as empty string; otherwise, -Infinity for numbers
        : a[orderBy];

    // Handle null or undefined values for valueB - THIS WAS THE BUG
    const valueB = b[orderBy] === null || b[orderBy] === undefined
        ? (typeof b[orderBy] === 'string' ? '' : -Infinity) // If string, treat as empty string; otherwise, -Infinity for numbers
        : b[orderBy]; // Corrected: should use b[orderBy] here

    if (valueB < valueA) {
        return -1;
    }
    if (valueB > valueA) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return (a, b) => {
        // Always sort favorites first (IsFavorite === true or 1)
        const isAFavorite = a.IsFavorite === true || a.IsFavorite === 1;
        const isBFavorite = b.IsFavorite === true || b.IsFavorite === 1;

        if (isAFavorite && !isBFavorite) {
            return -1; // A (favorite) comes before B (not favorite)
        }
        if (!isAFavorite && isBFavorite) {
            return 1; // B (favorite) comes before A (not favorite)
        }

        // If both have same favorite status, apply the secondary sort
        const comparison = descendingComparator(a, b, orderBy);
        return order === 'desc' ? comparison : -comparison;
    };
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}
// --- End Sorting Helpers ---


// --- Initial Head Cells (Matches new backend data keys) ---
// IMPORTANT: Removed 'IsFavorite' from here. It will be added explicitly in the EnhancedTableHead.
const initialHeadCells = [
    { id: 'BrandName', numeric: false, disablePadding: false, label: 'MARCA' },
    { id: 'ProductDescription', numeric: false, disablePadding: true, label: 'DESCRIPCION' },
    { id: 'ProductCode', numeric: false, disablePadding: false, label: 'CODIGO' },
    { id: 'EAN', numeric: true, disablePadding: false, label: 'EAN' },
    { id: 'RetailPrice', numeric: true, disablePadding: false, label: 'PRECIO' },
];
// --- End Initial Head Cells ---


// --- EnhancedTableHead Component ---
function EnhancedTableHead(props) {
    const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort, headCellsToRender, hideSelection = false, hideFavorites = false } = props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {/* Checkbox cell for select all */}
                {!hideSelection && (
                <StyledTableCell padding="checkbox">
                    <Checkbox
                        color="primary"
                        indeterminate={numSelected > 0 && numSelected < rowCount}
                        checked={rowCount > 0 && numSelected === rowCount}
                        onChange={onSelectAllClick}
                        inputProps={{
                            'aria-label': 'select all products in table',
                        }}
                    />
                </StyledTableCell>
                )}
                {/* NEW: Favorite column header (explicitly added) */}
                {!hideFavorites && (
                <StyledTableCell key="IsFavorite" align="center" padding="none">
                    <TableSortLabel
                        active={orderBy === 'IsFavorite'}
                        direction={orderBy === 'IsFavorite' ? order : 'asc'}
                        onClick={createSortHandler('IsFavorite')}
                    >
                        DESTACADO
                        {orderBy === 'IsFavorite' ? (
                            <Box component="span" sx={visuallyHidden}>
                                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                            </Box>
                        ) : null}
                    </TableSortLabel>
                </StyledTableCell>
                )}
                {/* Map through the passed-in headCellsToRender */}
                {headCellsToRender.map((headCell) => (
                    <StyledTableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </StyledTableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
    headCellsToRender: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        numeric: PropTypes.bool.isRequired,
        disablePadding: PropTypes.bool.isRequired,
        label: PropTypes.string.isRequired,
    })).isRequired,
};
// --- End EnhancedTableHead Component ---


// --- EnhancedTableToolbar Component (with Add to Cart & Clear Selection) ---
function EnhancedTableToolbar(props) {
    const { numSelected, onClearSelection, onAddToCart, currentSearchTerm, hideSelection = false, totemMode = false, totalRowCount = 0, rowsPerPage = 10 } = props;
    const totalPages = Math.ceil(totalRowCount / rowsPerPage);

    return (
        <Toolbar
            sx={[
                {
                    pl: { sm: 2 },
                    pr: { xs: 1, sm: 1 },
                },
                !hideSelection && numSelected > 0 && {
                    bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
                },
            ]}
        >
            {!hideSelection && numSelected > 0 ? (
                <Typography
                    sx={{ flex: '1 1 100%' }}
                    color="inherit"
                    variant="subtitle1"
                    component="div"
                >
                    {numSelected} productos seleccionados en la tabla
                </Typography>
            ) : (
                <Typography
                    sx={{ flex: '1 1 100%', fontWeight: 'bold' }}
                    variant="h6"
                    id="tableTitle"
                    component="div"
                >
                    <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <span>PRODUCTOS BUSCANDO: <span style={{ color: 'goldenrod', fontWeight: 'bold' }}>{currentSearchTerm}</span></span>
                        {totemMode && totalPages > 1 && (
                            <span style={{ fontSize: '0.75em', fontWeight: 'normal', color: '#666', marginLeft: '16px' }}>
                                ( <strong>←→</strong> para cambiar página)
                            </span>
                        )}
                    </span>
                </Typography>
            )}

            {/* --- NEW: Add to Cart Button --- */}
            {!hideSelection && numSelected > 0 && onAddToCart && (
                <Tooltip title="Agregar productos seleccionados al carrito">
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={onAddToCart}
                        style={{ marginLeft: '20px', border: '1px solid white', color: 'white', backgroundColor: '#4775ba' }}
                        endIcon={<AddShoppingCartIcon sx={{ color: 'white' }} fontSize='large' />}
                    >
                        AGREGAR
                    </Button>
                </Tooltip>
            )}

            {/* Clear Selection Button - Repurposed */}
            {!hideSelection && numSelected > 0 && onClearSelection && (
                <Tooltip title="Quitar productos seleccionados de la tabla">
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={onClearSelection}
                        style={{ marginLeft: '10px', border: '1px solid white', color: 'white', backgroundColor: '#e38511' }}
                        endIcon={<RemoveShoppingCartIcon sx={{ color: 'white' }} fontSize='large' />}
                    >
                        QUITAR
                    </Button>
                </Tooltip>
            )}
            {!hideSelection && numSelected === 0 && (
                <Tooltip title="No hay productos seleccionados en la tabla">
                    <IconButton>
                        {/* <DeleteIcon fontSize='large' color="disabled" /> */}
                    </IconButton>
                </Tooltip>
            )}
        </Toolbar>
    );
}

EnhancedTableToolbar.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onClearSelection: PropTypes.func.isRequired,
    onAddToCart: PropTypes.func.isRequired,
    currentSearchTerm: PropTypes.string,
};
// --- End EnhancedTableToolbar Component ---


// --- Default Settings ---
const DEFAULT_ORDER = 'asc';
const DEFAULT_ORDER_BY = 'BrandName';
const DEFAULT_ROWS_PER_PAGE = 10;
// --- End Default Settings ---


// --- Main SearchTable Component ---
const SearchTable = React.forwardRef(function SearchTable({ 
    data = [], 
    hasStockData = false, 
    selectedItems = [], 
    onToggleItem = () => {}, 
    onToggleAllItems = () => {}, 
    onAddToCart = () => {}, 
    onClearSelection = () => {}, 
    currentSearchTerm = '', 
    onToggleFavorite = () => {},
    hideSelection = false,
    hideFavorites = false,
    totemMode = false 
}, ref) {
    // State for sorting and pagination (remains local to the table)
    const [order, setOrder] = React.useState(DEFAULT_ORDER);
    const [orderBy, setOrderBy] = React.useState(DEFAULT_ORDER_BY);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(totemMode ? 5 : DEFAULT_ROWS_PER_PAGE);
    const [dense, setDense] = React.useState(false);

    // Check if user belongs to CIA organization (prices without decimals)
    const { user } = useAuth();
    const hideDecimals = user?.organizationName?.toUpperCase().includes('CIA');
    // GIULIANO feature: si el user tiene flag creditCardMarkupEnabled, mostrar columna RetailCardPrice
    const showCardPriceColumn = !!user?.creditCardMarkupEnabled;

    // --- Dynamically determine head cells based on hasStockData prop ---
    const dynamicHeadCells = React.useMemo(() => {
        let currentHeadCells = [...initialHeadCells];
        if (totemMode) {
            currentHeadCells = currentHeadCells.filter(c => c.id !== 'EAN');
        }
        // GIULIANO: agregar columna RetailCardPrice despues de RetailPrice si el flag esta activo
        if (showCardPriceColumn) {
            const idx = currentHeadCells.findIndex(c => c.id === 'RetailPrice');
            if (idx !== -1) {
                currentHeadCells.splice(idx + 1, 0, { id: 'RetailCardPrice', numeric: true, disablePadding: false, label: 'TARJETA' });
            }
        }
        if (hasStockData) {
            currentHeadCells.push({ id: 'IndividualUnitCount', numeric: true, disablePadding: false, label: 'UN.' });
        }
        return currentHeadCells;
    }, [hasStockData, totemMode, showCardPriceColumn]);


    // --- Data source is the 'data' prop ---
    const rows = Array.isArray(data) ? data : [];

    // Expose pagination controls to parent via ref
    React.useImperativeHandle(ref, () => ({
        nextPage: () => {
            setPage(prev => {
                const maxPage = Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1);
                return prev < maxPage ? prev + 1 : prev;
            });
        },
        prevPage: () => {
            setPage(prev => prev > 0 ? prev - 1 : 0);
        },
    }), [rows.length, rowsPerPage]);

    // Memoize ProductIds of all rows in the table for the "Select All" functionality
    const allRowProductIds = React.useMemo(() => rows.map(row => row.ProductId).filter(id => id != null), [rows]);


    // --- Memoize visibleRows calculation ---
    const visibleRows = React.useMemo(
        () => {
            const dataToProcess = Array.isArray(rows) ? rows : [];
            return stableSort(
                dataToProcess,
                getComparator(order, orderBy),
            ).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
        },
        [rows, order, orderBy, page, rowsPerPage],
    );


    // --- Handlers using useCallback for stability ---
    // Sorting handlers remain local
    const handleRequestSort = React.useCallback(
        (event, property) => {
            const isAsc = orderBy === property && order === 'asc';
            const toggledOrder = isAsc ? 'desc' : 'asc';
            setOrder(toggledOrder);
            setOrderBy(property);
            setPage(0); // Reset page to 0 when sorting changes
        },
        [order, orderBy],
    );

    // Pagination handlers remain local
    const handleChangePage = React.useCallback(
        (event, newPage) => {
            setPage(newPage);
        },
        [],
    );

    const handleChangeRowsPerPage = React.useCallback(
        (event) => {
            const updatedRowsPerPage = parseInt(event.target.value, 10);
            setRowsPerPage(updatedRowsPerPage);
            setPage(0); // Reset to the first page
        },
        [],
    );

    // Dense padding handler remains local
    const handleChangeDense = React.useCallback(
        (event) => {
            setDense(event.target.checked);
        },
        [],
    );

    // --- Row Selection Handlers (Call parent handlers) ---

    const handleSelectAllClick = React.useCallback((event) => {
        onToggleAllItems(event.target.checked, allRowProductIds);
    }, [onToggleAllItems, allRowProductIds]);


    const handleClick = React.useCallback((event, productId) => {
        const isCurrentlySelected = selectedItems.includes(productId);
        onToggleItem(productId, !isCurrentlySelected);

    }, [selectedItems, onToggleItem]);

    // Helper function to check if a row is selected (uses the prop)
    const isSelected = React.useCallback((productId) => selectedItems.includes(productId), [selectedItems]);
    // --- End Row Selection Handlers ---


    // Calculate total number of rows for pagination (uses the data prop length)
    const totalRowCount = rows.length;

    // Responsive pivot breakpoint (live)
    // Totem runs at 1080px portrait — use a lower breakpoint so it renders the full table.
    // All other pages keep the original 1660px threshold.
    const theme = useTheme();
    const isPivotDefault = useMediaQuery('(max-width:1660px)');
    const isPivotTotem  = useMediaQuery('(max-width:1050px)');
    const isPivot = totemMode ? isPivotTotem : isPivotDefault;
    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - totalRowCount) : 0;


    return (
        <div className='boxdiv'>
            <Box>
                <Paper sx={{ width: '78vw', mb: 2, mx: 'auto' }}>
                    <EnhancedTableToolbar
                        numSelected={selectedItems.length}
                        onAddToCart={onAddToCart}
                        onClearSelection={onClearSelection}
                        currentSearchTerm={currentSearchTerm}
                        hideSelection={hideSelection}
                        totemMode={totemMode}
                        totalRowCount={totalRowCount}
                        rowsPerPage={rowsPerPage}
                    />
                    {/* Render PivotList for narrow viewports (<1660px) to avoid brittle CSS pivoting */}
                    {isPivot ? (
                        <PivotList
                            rows={visibleRows}
                            selectedItems={selectedItems}
                            onToggleItem={(id, checked) => onToggleItem(id, checked)}
                            onToggleFavorite={onToggleFavorite}
                            hasStockData={hasStockData}
                            hideDecimals={hideDecimals}
                            totemMode={totemMode}
                        />
                    ) : (
                        <TableContainer>
                            <Table
                                sx={{ width: '100%', minWidth: 0 }}
                                aria-labelledby="Titulo de Tabla"
                                size={dense ? 'small' : 'medium'}
                            >
                                <colgroup>
                                    {!hideSelection && <col style={{ width: '3%' }} />}
                                    {!hideFavorites && <col style={{ width: '2%' }} />}
                                    {totemMode ? (
                                        <>
                                            <col style={{ width: '15%' }} />
                                            <col style={{ width: '55%' }} />
                                            <col style={{ width: '10%' }} />
                                            <col style={{ width: '20%' }} />
                                        </>
                                    ) : (
                                        <>
                                            <col style={{ width: (hideSelection && hideFavorites) ? '12%' : '2%' }} />
                                            <col style={{ width: hasStockData ? '44%' : '52%' }} />
                                            <col style={{ width: (hideSelection && hideFavorites) ? '5%' : '6%' }} />
                                            <col style={{ width: (hideSelection && hideFavorites) ? '14%' : '18%' }} />
                                            <col style={{ width: '14%' }} />
                                            {hasStockData && <col style={{ width: '10%' }} />}
                                        </>
                                    )}
                                </colgroup>
                                <EnhancedTableHead
                                    numSelected={selectedItems.length}
                                    order={order}
                                    orderBy={orderBy}
                                    onSelectAllClick={handleSelectAllClick}
                                    onRequestSort={handleRequestSort}
                                    rowCount={totalRowCount}
                                    headCellsToRender={dynamicHeadCells}
                                    hideSelection={hideSelection}
                                    hideFavorites={hideFavorites}
                                />
                                <TableBody>
                                    {Array.isArray(visibleRows) && visibleRows.map((row, index) => {
                                        const isRowSelected = isSelected(row.ProductId);
                                        const labelId = `enhanced-table-checkbox-${row.ProductId || index}`;

                                        return (
                                            <StyledTableRow
                                                hover
                                                onClick={!hideSelection && row.ProductId != null ? (event) => handleClick(event, row.ProductId) : undefined}
                                                role={hideSelection ? undefined : "checkbox"}
                                                aria-checked={hideSelection ? undefined : isRowSelected}
                                                tabIndex={-1}
                                                key={row.ProductId || index}
                                                selected={!hideSelection && isRowSelected}
                                                sx={{ cursor: !hideSelection && row.ProductId != null ? 'pointer' : 'default' }}
                                            >
                                                {!hideSelection && (
                                                <StyledTableCell padding="checkbox" data-label="Seleccionar" className="select-inline">
                                                    {row.ProductId != null && (
                                                        <Checkbox
                                                            color="primary"
                                                            checked={isRowSelected}
                                                            inputProps={{ 'aria-labelledby': labelId }}
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                handleClick(event, row.ProductId);
                                                            }}
                                                        />
                                                    )}
                                                </StyledTableCell>
                                                )}
                                                {/* --- Favorite Icon Cell (Always first content column after checkbox) --- */}
                                                {!hideFavorites && (
                                                <StyledTableCell align="center" data-label="Destacado" className="favorite-inline">
                                                    <Tooltip title={row.IsFavorite ? "Quitar de favoritos" : "Agregar a favoritos"}>
                                                        <IconButton
                                                            aria-label="toggle favorite"
                                                            size="small"
                                                            onClick={(event) => {
                                                                event.stopPropagation(); // Prevents row click from firing
                                                                onToggleFavorite(row.ProductId, row.IsFavorite);
                                                            }}
                                                        >
                                                            {/* CHANGED: Check for boolean 'true' instead of number '1' */}
                                                            {row.IsFavorite === true ? (
                                                                <StarIcon sx={{ color: 'black' }} /> // Filled star for favorite
                                                            ) : (
                                                                <StarBorderIcon sx={{ color: 'black' }} /> // Outline star for not favorite
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>
                                                </StyledTableCell>
                                                )}
                                                {/* --- End Favorite Icon Cell --- */}

                                                {/* --- Display data columns based on initialHeadCells --- */}
                                                <StyledTableCell component="th" scope="row" id={labelId} data-label="Marca" className="brand-inline">
                                                    {row.BrandName != null ? row.BrandName : '-'}
                                                </StyledTableCell>
                                                <StyledTableCell align="left" data-label="Descripcion">
                                                    {row.ProductDescription != null ? row.ProductDescription : '-'}
                                                </StyledTableCell>
                                                <StyledTableCell align="left" data-label="Codigo">
                                                    {row.ProductCode != null ? row.ProductCode : '-'}
                                                </StyledTableCell>
                                                {!totemMode && (
                                                <StyledTableCell align="right" data-label="EAN">
                                                    {row.EAN != null ? row.EAN : '-'}
                                                </StyledTableCell>
                                                )}
                                                <StyledTableCell align="right" data-label="Precio" sx={totemMode ? { fontWeight: 'bold', fontSize: '1.5em' } : undefined}>
                                                    {`$ ${row.RetailPrice != null ? parseFloat(row.RetailPrice).toFixed(hideDecimals ? 0 : 2) : 'N/A'}`}
                                                </StyledTableCell>
                                                {showCardPriceColumn && (
                                                    <StyledTableCell align="right" data-label="Precio Credito" sx={totemMode ? { fontWeight: 'bold', fontSize: '1.5em' } : undefined}>
                                                        {`$ ${row.RetailCardPrice != null ? parseFloat(row.RetailCardPrice).toFixed(hideDecimals ? 0 : 2) : 'N/A'}`}
                                                    </StyledTableCell>
                                                )}
                                                {/* --- Conditionally display Individual Unit Count cell --- */}
                                                {hasStockData && (
                                                    <StyledTableCell align="right" data-label="Un.">
                                                        {row.IndividualUnitCount != null ? row.IndividualUnitCount.toLocaleString('es-AR') : '-'}
                                                    </StyledTableCell>
                                                )}
                                                {/* --- End Conditional Individual Unit Count cell --- */}
                                            </StyledTableRow>
                                        );
                                    })}

                                    {/* Render message when no data is found */}
                                    {Array.isArray(visibleRows) && visibleRows.length === 0 && totalRowCount === 0 && (
                                        <StyledTableRow style={{ height: (dense ? 33 : 53) * (rowsPerPage) }}>
                                            <StyledTableCell colSpan={dynamicHeadCells.length + 2} sx={{ textAlign: 'center' }}>
                                                No se encontraron productos con esa búsqueda.
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    )}

                                    {/* Render empty rows to maintain table height */}
                                    {emptyRows > 0 && (
                                        <StyledTableRow
                                            style={{
                                                height: (dense ? 33 : 53) * emptyRows,
                                            }}
                                        >
                                            <StyledTableCell colSpan={dynamicHeadCells.length + 2} />
                                        </StyledTableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    <TablePagination
                        labelRowsPerPage="Resultados por pagina"
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        component="div"
                        count={totalRowCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
                <FormControlLabel
                    control={<Switch checked={dense} onChange={handleChangeDense} />}
                    label="Compactar"
                />
            </Box>
        </div>
    );
});

// Update PropTypes for the new props
SearchTable.propTypes = {
    data: PropTypes.array,
    hasStockData: PropTypes.bool,
    selectedItems: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    onToggleItem: PropTypes.func,
    onToggleAllItems: PropTypes.func,
    onAddToCart: PropTypes.func,
    onClearSelection: PropTypes.func,
    currentSearchTerm: PropTypes.string,
    onToggleFavorite: PropTypes.func,
    hideSelection: PropTypes.bool,
    hideFavorites: PropTypes.bool,
    totemMode: PropTypes.bool,
};

export default SearchTable;

